"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
interface FooterArtProps {
  isInView: boolean;
  isEmber: boolean;
  isNeumorphic: boolean;
}
export default function Footer3D(t0) {
  const $ = _c(7);
  const {
    isInView,
    isEmber,
    isNeumorphic
  } = t0;
  const canvasRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      x: -1000,
      y: -1000
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mouseRef = useRef(t1);
  let t2;
  let t3;
  if ($[1] !== isEmber || $[2] !== isInView || $[3] !== isNeumorphic) {
    t2 = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }
      let animationFrameId;
      let width = 0;
      let height = 0;
      const resize = () => {
        const rect = canvas.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);
      };
      resize();
      window.addEventListener("resize", resize);
      const handleMouseMove = e => {
        const rect_0 = canvas.getBoundingClientRect();
        mouseRef.current = {
          x: e.clientX - rect_0.left,
          y: e.clientY - rect_0.top
        };
      };
      const handleMouseLeave = () => {
        mouseRef.current = {
          x: -1000,
          y: -1000
        };
      };
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseleave", handleMouseLeave);
      const isMobile = window.innerWidth < 768;
      const particleCount = isMobile ? 32 : 65;
      const particles = Array.from({
        length: particleCount
      }, () => ({
        x: Math.random() * (width || 800),
        y: Math.random() * (height || 400),
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 1.8 + 1.2,
        baseAlpha: Math.random() * 0.5 + 0.3
      }));
      let time = 0;
      const render = () => {
        if (!isInView) {
          return;
        }
        time = time + 0.012;
        time;
        ctx.clearRect(0, 0, width, height);
        let primaryColor = "255, 255, 255";
        let accentColor = "0, 240, 255";
        let gridAlpha = 0.14;
        if (isEmber) {
          primaryColor = "255, 79, 0";
          accentColor = "255, 140, 0";
          gridAlpha = 0.22;
        } else {
          if (isNeumorphic) {
            primaryColor = "55, 65, 81";
            accentColor = "31, 41, 55";
            gridAlpha = 0.16;
          }
        }
        ctx.save();
        ctx.lineWidth = 1;
        const horizonY = height * 0.35;
        for (let i = 0; i < 12; i++) {
          const progress = i / 12;
          const y = horizonY + Math.pow(progress, 1.8) * (height - horizonY);
          const lineAlpha = (1 - Math.abs(progress - 0.5) * 1.4) * gridAlpha;
          ctx.strokeStyle = `rgba(${primaryColor}, ${Math.max(0, lineAlpha)})`;
          ctx.beginPath();
          const step = isMobile ? 24 : 14;
          for (let x = 0; x <= width; x = x + step, x) {
            const wave = Math.sin(time + x * 0.007 + i * 0.45) * (4 + i * 1.3);
            if (x === 0) {
              ctx.moveTo(x, y + wave);
            } else {
              ctx.lineTo(x, y + wave);
            }
          }
          ctx.stroke();
        }
        ctx.restore();
        ctx.save();
        const perspectiveCols = isMobile ? 12 : 24;
        const centerX = width / 2;
        ctx.lineWidth = 1;
        ctx.strokeStyle = `rgba(${primaryColor}, ${gridAlpha * 0.75})`;
        for (let i_0 = 0; i_0 <= perspectiveCols; i_0++) {
          const xFactor = (i_0 / perspectiveCols - 0.5) * 2;
          const startX = centerX + xFactor * (width * 0.22);
          const endX = centerX + xFactor * (width * 0.95);
          ctx.beginPath();
          ctx.moveTo(startX, horizonY);
          ctx.lineTo(endX, height);
          ctx.stroke();
        }
        ctx.restore();
        const mouse = mouseRef.current;
        const connectionDist = isMobile ? 85 : 125;
        for (let i_1 = 0; i_1 < particleCount; i_1++) {
          const p = particles[i_1];
          p.x = p.x + p.vx;
          p.y = p.y + p.vy;
          if (p.x < 0) {
            p.x = width;
          }
          if (p.x > width) {
            p.x = 0;
          }
          if (p.y < 0) {
            p.y = height;
          }
          if (p.y > height) {
            p.y = 0;
          }
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 130 && dist > 2) {
            const force = (1 - dist / 130) * 1.8;
            p.x = p.x + dx / dist * force;
            p.y = p.y + dy / dist * force;
          }
          if (dist < 130) {
            const mouseAlpha = (1 - dist / 130) * 0.45;
            ctx.strokeStyle = `rgba(${accentColor}, ${mouseAlpha})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
          const pulse = Math.sin(time * 2 + i_1) * 0.2 + 0.8;
          ctx.fillStyle = `rgba(${primaryColor}, ${p.baseAlpha * pulse})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fill();
          for (let j = i_1 + 1; j < particleCount; j++) {
            const p2 = particles[j];
            const pDistance = Math.hypot(p.x - p2.x, p.y - p2.y);
            if (pDistance < connectionDist) {
              const alpha = (1 - pDistance / connectionDist) * 0.22;
              ctx.strokeStyle = `rgba(${accentColor}, ${alpha})`;
              ctx.lineWidth = 0.8;
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          }
        }
        animationFrameId = requestAnimationFrame(render);
      };
      render();
      return () => {
        window.removeEventListener("resize", resize);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseleave", handleMouseLeave);
        cancelAnimationFrame(animationFrameId);
      };
    };
    t3 = [isInView, isEmber, isNeumorphic];
    $[1] = isEmber;
    $[2] = isInView;
    $[3] = isNeumorphic;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <canvas ref={canvasRef} className="w-full h-full block pointer-events-auto" style={{
      touchAction: "none"
    }} />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}